import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accessibility, Plus, Minus, RotateCcw, X, Eye,
  Type, MousePointer2, Link2, Palette, Sun, Moon,
  ImageOff, Pause, Keyboard, ExternalLink
} from 'lucide-react';

interface AccessibilityState {
  fontSize: number;
  lineSpacing: number;
  largeCursor: boolean;
  highlightLinks: boolean;
  grayscale: boolean;
  highContrast: boolean;
  lightBackground: boolean;
  darkBackground: boolean;
  hideImages: boolean;
  readableFont: boolean;
  stopAnimations: boolean;
  keyboardNav: boolean;
}

const defaultState: AccessibilityState = {
  fontSize: 100,
  lineSpacing: 100,
  largeCursor: false,
  highlightLinks: false,
  grayscale: false,
  highContrast: false,
  lightBackground: false,
  darkBackground: false,
  hideImages: false,
  readableFont: false,
  stopAnimations: false,
  keyboardNav: false,
};

export default function AccessibilityWidget() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<AccessibilityState>(() => {
    try {
      const saved = localStorage.getItem('kipikid-accessibility');
      return saved ? { ...defaultState, ...JSON.parse(saved) } : defaultState;
    } catch {
      return defaultState;
    }
  });

  useEffect(() => {
    localStorage.setItem('kipikid-accessibility', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    document.documentElement.style.fontSize = `${state.fontSize}%`;
  }, [state.fontSize]);

  useEffect(() => {
    if (state.lineSpacing !== 100) {
      document.documentElement.style.lineHeight = `${state.lineSpacing / 100 * 1.5}`;
    } else {
      document.documentElement.style.lineHeight = '';
    }
  }, [state.lineSpacing]);

  useEffect(() => {
    const cl = document.documentElement.classList;
    cl.toggle('acc-large-cursor', state.largeCursor);
    cl.toggle('acc-highlight-links', state.highlightLinks);
    cl.toggle('acc-grayscale', state.grayscale);
    cl.toggle('acc-high-contrast', state.highContrast);
    cl.toggle('acc-light-bg', state.lightBackground);
    cl.toggle('acc-dark-bg', state.darkBackground);
    cl.toggle('acc-hide-images', state.hideImages);
    cl.toggle('acc-readable-font', state.readableFont);
    cl.toggle('acc-stop-animations', state.stopAnimations);
    cl.toggle('acc-keyboard-nav', state.keyboardNav);
    cl.toggle('high-contrast', state.highContrast);
    cl.toggle('reduce-motion', state.stopAnimations);
  }, [state]);

  // Keyboard shortcut: Ctrl+U to toggle widget (SI 5568 recommendation)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  const update = useCallback((key: keyof AccessibilityState, value: any) => {
    setState(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'lightBackground' && value) next.darkBackground = false;
      if (key === 'darkBackground' && value) next.lightBackground = false;
      if (key === 'highContrast' && value) { next.lightBackground = false; next.darkBackground = false; }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setState(defaultState);
    document.documentElement.style.fontSize = '100%';
    document.documentElement.style.lineHeight = '';
  }, []);

  const activeCount = Object.entries(state).filter(([k, v]) => {
    if (k === 'fontSize' || k === 'lineSpacing') return v !== 100;
    return v === true;
  }).length;

  const ToggleButton = ({ icon: Icon, label, active, onClick }: {
    icon: any; label: string; active: boolean; onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full p-2.5 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
        active
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
      }`}
      role="switch"
      aria-checked={active}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  );

  const StepperControl = ({ label, value, min, max, step, onChange }: {
    label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void;
  }) => (
    <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="flex items-center gap-1.5" role="group" aria-label={label}>
        <button
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={`${t('accessibility.decrease')} ${label}`}
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="text-sm font-semibold text-gray-900 w-12 text-center tabular-nums" aria-live="polite">{value}%</span>
        <button
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={`${t('accessibility.increase')} ${label}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-5 start-5 z-[9999]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 start-0 bg-white rounded-2xl shadow-2xl border border-gray-200 w-80 max-h-[80vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label={t('accessibility.title')}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Accessibility className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-sm">{t('accessibility.title')}</h2>
                  <p className="text-[10px] text-gray-400">{t('accessibility.standard')}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={t('accessibility.close')}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-3 space-y-2 flex-1">
              <StepperControl
                label={t('accessibility.fontSize')}
                value={state.fontSize}
                min={80}
                max={200}
                step={10}
                onChange={v => update('fontSize', v)}
              />

              <StepperControl
                label={t('accessibility.lineSpacing')}
                value={state.lineSpacing}
                min={100}
                max={200}
                step={25}
                onChange={v => update('lineSpacing', v)}
              />

              <div className="grid grid-cols-2 gap-1.5 pt-1">
                <ToggleButton
                  icon={MousePointer2}
                  label={t('accessibility.largeCursor')}
                  active={state.largeCursor}
                  onClick={() => update('largeCursor', !state.largeCursor)}
                />
                <ToggleButton
                  icon={Link2}
                  label={t('accessibility.highlightLinks')}
                  active={state.highlightLinks}
                  onClick={() => update('highlightLinks', !state.highlightLinks)}
                />
                <ToggleButton
                  icon={Palette}
                  label={t('accessibility.grayscale')}
                  active={state.grayscale}
                  onClick={() => update('grayscale', !state.grayscale)}
                />
                <ToggleButton
                  icon={Eye}
                  label={t('accessibility.highContrast')}
                  active={state.highContrast}
                  onClick={() => update('highContrast', !state.highContrast)}
                />
                <ToggleButton
                  icon={Sun}
                  label={t('accessibility.lightBg')}
                  active={state.lightBackground}
                  onClick={() => update('lightBackground', !state.lightBackground)}
                />
                <ToggleButton
                  icon={Moon}
                  label={t('accessibility.darkBg')}
                  active={state.darkBackground}
                  onClick={() => update('darkBackground', !state.darkBackground)}
                />
                <ToggleButton
                  icon={ImageOff}
                  label={t('accessibility.hideImages')}
                  active={state.hideImages}
                  onClick={() => update('hideImages', !state.hideImages)}
                />
                <ToggleButton
                  icon={Type}
                  label={t('accessibility.readableFont')}
                  active={state.readableFont}
                  onClick={() => update('readableFont', !state.readableFont)}
                />
                <ToggleButton
                  icon={Pause}
                  label={t('accessibility.stopAnimations')}
                  active={state.stopAnimations}
                  onClick={() => update('stopAnimations', !state.stopAnimations)}
                />
                <ToggleButton
                  icon={Keyboard}
                  label={t('accessibility.keyboardNav')}
                  active={state.keyboardNav}
                  onClick={() => update('keyboardNav', !state.keyboardNav)}
                />
              </div>
            </div>

            {/* Footer with reset + accessibility statement */}
            <div className="p-3 border-t border-gray-100 space-y-1.5">
              <button
                onClick={reset}
                className="w-full flex items-center justify-center gap-2 p-2.5 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                {t('accessibility.reset')}
              </button>
              <a
                href="#accessibility-statement"
                className="w-full flex items-center justify-center gap-1.5 p-2 text-xs text-blue-600 hover:text-blue-700 hover:underline rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
                הצהרת נגישות
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trigger Button — always visible, meets min 44x44 tap target (WCAG 2.1) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 hover:scale-105 transition-all flex items-center justify-center relative focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label={`${t('accessibility.title')} (Ctrl+U)`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Accessibility className="w-6 h-6" />
        {activeCount > 0 && (
          <span className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm" aria-label={`${activeCount} active`}>
            {activeCount}
          </span>
        )}
      </button>
    </div>
  );
}
